// Base imports
import { Component } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import Vue from 'vue';
import { RegistrationFlow, UiNode, UiNodeInputAttributes } from '@ory/client';
import { AxiosError } from 'axios';
import MDTextFieldWithValidation from '@/components/Validation/MDTextFieldWithValidation';
import store from '@/store';
import user from '@/store/modules/user';
import notifier from '@/_helpers/notifier';
import { feApi } from '@/_helpers/ory';
import { env, router } from '@/_helpers';
import { setParam } from '@/_helpers/url';
import template from './Register.vue';

@Component({
	mixins: [template],
	components: {
		ValidationObserver,
		MDTextFieldWithValidation
	}
})
export default class Register extends Vue {
	loading: boolean = true;

	form = {
		csrf_token: '',
		traits: {
			name: '',
			email: '',
			localization: {
				language: 'nl'
			},
			token: ''
		},
		password: '',
		method: ''
	};

	isRegistered: boolean = false;

	sessionAlreadyAvailable: boolean = false;

	tokenExpired: boolean = false;

	tokenNotFound: boolean = false;

	showPassword: boolean = false;

	flow: RegistrationFlow | null = null;

	async mounted(): Promise<void> {
		this.form.traits.email = this.$route.query.email as string;
		this.form.traits.token = this.$route.params.token as string;

		const promFlow: Promise<void> = this.getFlow();
		const promToken: Promise<void> = this.getToken();

		await Promise.all([promFlow, promToken]);

		this.loading = false;
	}

	async getFlow(): Promise<void> {
		const flowID: string = this.$route.query.flow as string; // Get Flow Review
		let getNewFlowID: boolean = flowID === '' || flowID === undefined;

		if (!getNewFlowID) {
			await feApi
				.getRegistrationFlow({ id: flowID })
				.then((response) => {
					this.setFlow(response.data);
				})
				.catch((error: AxiosError) => {
					switch (error.response.status) {
						case 400:
							switch (error.response.data.error.id) {
								case 'session_already_available':
									this.sessionAlreadyAvailable = true;
									break;
								default:
									getNewFlowID = true;
									break;
							}
							break;
						case 410:
						default:
							getNewFlowID = true;
					}
				});
		}

		if (getNewFlowID) {
			await feApi
				.createBrowserRegistrationFlow()
				.then((response) => {
					this.setFlow(response.data);
				})
				.catch((error: AxiosError) => {
					let throwErr: boolean = false;

					switch (error.response.status) {
						case 400:
							switch (error.response.data.error.id) {
								case 'session_already_available':
									this.sessionAlreadyAvailable = true;
									break;
								default:
									throwErr = true;
									break;
							}
							break;
						default:
							throwErr = true;
							break;
					}

					if (throwErr) {
						notifier.error('getting', 'data');
					}
				});

			setParam('email', this.form.traits.email);
		}
	}

	setFlow(flow: RegistrationFlow) {
		flow.ui.nodes.forEach((value: UiNode, index: number, array: Array<any>) => {
			const node: UiNodeInputAttributes = value.attributes as UiNodeInputAttributes;
			switch (node.name) {
				case 'csrf_token':
					this.form.csrf_token = node.value;
					break;
				case 'method':
					this.form.method = node.value;
					break;
				default:
					break;
			}
		});

		setParam('flow', flow.id);

		this.flow = flow;
	}

	async getToken(): Promise<void> {
		try {
			await user.validate(this.form.traits.token);
		} catch (error) {
			// @ts-ignore
			this.checkErrors(error.data.errors);
		}
	}

	checkErrors(errors: any): void {
		try {
			if (errors[0].source.pointer === 'token') {
				if (errors[0].code === 'token_not_found') {
					this.tokenNotFound = true;
				} else if (errors[0].code === 'token_expired') {
					this.tokenExpired = true;
				}
			}
		} catch (e) {
			// Could happen, do nothing.
		}
	}

	get darkMode(): boolean {
		return store.state.darkMode;
	}

	async submit(): Promise<void> {
		if (this.loading) return;

		const isValid = await (this.$refs.form as any).validate();
		if (!isValid) {
			return;
		}

		this.loading = true;

		await feApi
			.updateRegistrationFlow({ flow: this.flow.id, updateRegistrationFlowBody: this.form })
			.then(() => {
				notifier.success('registered', 'user');

				this.isRegistered = true;
			})
			.catch(() => {
				notifier.error('getting', 'data');
			});

		this.loading = false;
	}
}
